import { toast } from '@/hooks/use-toast';

// Predefined reminder options in minutes
export const REMINDER_OPTIONS = [
  { value: 10, label: '10 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 60 * 24, label: '1 day before' },
  { value: 60 * 24 * 2, label: '2 days before' },
] as const;

class NotificationService {
  private static instance: NotificationService;
  private hasPermission: boolean = false;

  private constructor() {
    this.checkPermission();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private async checkPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.error('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.hasPermission = true;
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.hasPermission = permission === 'granted';
      return this.hasPermission;
    }

    return false;
  }

  public async requestPermission(): Promise<boolean> {
    return this.checkPermission();
  }

  public async scheduleNotification(taskId: string, title: string, description: string | undefined, dueDate: Date, reminderMinutes: number) {
    if (!this.hasPermission) {
      const granted = await this.requestPermission();
      if (!granted) {
        toast({
          title: "Notification Permission Required",
          description: "Please enable notifications to receive reminders",
          variant: "destructive"
        });
        return;
      }
    }

    const reminderTime = new Date(dueDate.getTime() - (reminderMinutes * 60 * 1000));
    
    // If the reminder time is in the past, don't schedule it
    if (reminderTime.getTime() <= Date.now()) {
      return;
    }

    try {
      // Register the reminder in the service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SCHEDULE_NOTIFICATION',
          payload: {
            taskId,
            title: `Reminder: ${title}`,
            body: description || 'Task due soon',
            timestamp: reminderTime.getTime(),
          },
        });
      }

      // Store the reminder in local storage as backup
      const reminders = JSON.parse(localStorage.getItem('taskReminders') || '{}');
      reminders[taskId] = {
        title,
        description,
        dueDate: dueDate.toISOString(),
        reminderTime: reminderTime.toISOString(),
      };
      localStorage.setItem('taskReminders', JSON.stringify(reminders));

    } catch (error) {
      console.error('Error scheduling notification:', error);
      toast({
        title: "Reminder Error",
        description: "Failed to schedule reminder. Please try again.",
        variant: "destructive"
      });
    }
  }

  public async cancelNotification(taskId: string) {
    try {
      // Remove from service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CANCEL_NOTIFICATION',
          payload: { taskId },
        });
      }

      // Remove from local storage
      const reminders = JSON.parse(localStorage.getItem('taskReminders') || '{}');
      delete reminders[taskId];
      localStorage.setItem('taskReminders', JSON.stringify(reminders));

    } catch (error) {
      console.error('Error canceling notification:', error);
    }
  }

  public async checkMissedReminders() {
    const reminders = JSON.parse(localStorage.getItem('taskReminders') || '{}');
    const now = Date.now();

    Object.entries(reminders).forEach(([taskId, reminder]: [string, any]) => {
      const reminderTime = new Date(reminder.reminderTime).getTime();
      if (reminderTime <= now && reminderTime > now - (24 * 60 * 60 * 1000)) { // Check last 24 hours
        // Show missed reminder
        new Notification(`Missed Reminder: ${reminder.title}`, {
          body: reminder.description || 'Task was due',
          icon: '/icon-192.png',
        });
        
        // Remove the reminder
        this.cancelNotification(taskId);
      }
    });
  }
}

export const notificationService = NotificationService.getInstance(); 